import { FC, Fragment, useEffect, useState } from 'react';
import { Column } from 'ts/shared/interfaces/Generic';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faMinus,
  faPlus,
  faSort,
  faSortDown,
  faSortUp,
} from '@fortawesome/free-solid-svg-icons';
import { getIcon, getPayment, getStatus } from 'constants/mo';
import { faCcVisa } from '@fortawesome/free-brands-svg-icons';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { addDays, parse } from 'date-fns';
import withErrorBoundary from '../withErrorBoundary';

interface FilterColums {
  field: string;
  value: string;
  active: boolean;
}

interface TableProps {
  data: any[];
  columns: Column[];
  onDoubleClick?: (index: number) => void;
  onClick?: (index: number) => void;
  activeIndex?: number;
  onPressArrow?: (direction: string) => void;
  hideSort?: boolean;
  overflow?: string;
}

const Table: FC<TableProps> = ({ columns, ...props }) => {
  const [t] = useTranslation();
  const [direction, setDirection] = useState<'asc' | 'desc' | undefined>();
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>();
  const [data, setData] = useState<any[]>([]);
  const [filterColumns, setFilterColumns] = useState<FilterColums[]>([]);
  const [showMore, setShowMore] = useState<string[]>([]);

  useEffect(() => {
    setData(props.data);
  }, [props.data]); // eslint-disable-line

  useEffect(() => {
    const newFilterColumns = columns
      .filter((column) => column.filter)
      .map((column) => {
        return { field: column.field, value: '', active: false };
      });
    setFilterColumns(newFilterColumns);
  }, []); // eslint-disable-line

  const filterTable = (column: string, val: string) => {
    const newFilterColumns = filterColumns.map((item) => {
      if (item.field === column) {
        return { ...item, value: val };
      }
      return item;
    });
    setFilterColumns(newFilterColumns);

    const newData = [...props.data].filter((item) => {
      return newFilterColumns.every((filter) => {
        if (filter.active) {
          return item[filter.field].toString().toLowerCase().includes(filter.value.toLowerCase());
        }
        return true;
      });
    });
    setData(newData);
  };

  const sortTable = (column: string) => {
    const newDirection = direction === 'desc' ? 'asc' : 'desc';
    const sortedItems = [...data].sort((a: any, b: any) => {
      const sort = (a: any, b: any) => {
        if (typeof a[column] === 'string') {
          return a[column]?.localeCompare(b[column]);
        } else if (typeof a[column] === 'number') {
          return a[column] - b[column];
        } else if (a[column] instanceof Date) {
          return a[column].getTime() - b[column].getTime();
        }
        return 0;
      };

      return newDirection === 'asc' ? sort(a, b) : sort(b, a);
    });
    setSelectedColumn(column);
    setDirection(newDirection);
    setData(sortedItems);
  };

  const onClickFilter = (column: string) => {
    const newFilterColumns = filterColumns.map((item) => {
      if (item.field === column) {
        return { ...item, active: !item.active, value: item.value };
      }
      return item;
    });
    setFilterColumns(newFilterColumns);
  };

  const clearFilters = () => {
    const newFilterColumns = filterColumns.map((item) => {
      return { ...item, active: false, value: '' };
    });
    setFilterColumns(newFilterColumns);
    setData(JSON.parse(JSON.stringify(props.data)));
  };

  return (
    <div className="mo-table-wr" style={props.overflow ? { overflow: props.overflow } : {}}>
      {filterColumns.some((fil) => fil.value) && (
        <span className="text-xsm color-danger cur-poi mo-table-clear" onClick={clearFilters}>
          {t('filter.clear')}
        </span>
      )}

      <table
        className="mo-table"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            props.onPressArrow?.('down');
          }
          if (e.key === 'ArrowUp') {
            props.onPressArrow?.('up');
          }
        }}
      >
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th key={index} className={column.className}>
                <span className="mo-table-head">
                  {filterColumns.some((fil) => column.field === fil.field && fil.active) && (
                    <input
                      className="mo-table-filter-input"
                      onChange={(e) => filterTable(column.field, e.target.value)}
                      value={filterColumns.find((fil) => column.field === fil.field)?.value}
                    />
                  )}
                  {!filterColumns.some((fil) => column.field === fil.field && fil.active) && (
                    <span>{t(column.label)}</span>
                  )}

                  <span className="mo-table-actions">
                    {column.filter && (
                      <span className="mo-table-filter" onClick={() => onClickFilter(column.field)}>
                        <FontAwesomeIcon
                          className={
                            filterColumns.some((fil) => column.field === fil.field && fil.active)
                              ? 'text-default-color'
                              : ''
                          }
                          icon={faFilter}
                        />
                      </span>
                    )}

                    {!props.hideSort && (
                      <span
                        className="mo-table-sort"
                        onClick={() => sortTable(column.sortField || column.field)}
                      >
                        {selectedColumn !== (column.sortField || column.field) && (
                          <FontAwesomeIcon icon={faSort} className="mo-table-sort-inactive" />
                        )}
                        {selectedColumn === (column.sortField || column.field) && (
                          <Fragment>
                            {direction === 'asc' && (
                              <FontAwesomeIcon className="mo-sort-active" icon={faSortUp} />
                            )}
                            {direction === 'desc' && (
                              <FontAwesomeIcon className="mo-sort-active" icon={faSortDown} />
                            )}
                          </Fragment>
                        )}
                      </span>
                    )}
                  </span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              onDoubleClick={() => props.onDoubleClick?.(rowIndex)}
              onClick={() => props.onClick?.(rowIndex)}
              className={
                (props.activeIndex === rowIndex ? 'active' : '') +
                (parse(row.clx || '', 'dd/MM/yy', new Date()) < addDays(new Date(), 2)
                  ? ' mo-row-danger'
                  : '')
              }
            >
              {columns.map((column, index) => (
                <td
                  className={column.className}
                  key={index}
                  title={
                    column.format === 'jsx' || !row[column.field]
                      ? undefined
                      : row[column.field].toString()
                  }
                >
                  {column.format === 'icon' && (
                    <span>
                      <FontAwesomeIcon
                        icon={getIcon(row[column.field])}
                        className={column.helpField ? row[column.helpField]?.split('|')[0] : ''}
                      />
                      {column.helpField && row[column.helpField]?.split('|').length === 2 && (
                        <span className="ml-5">{row[column.helpField]?.split('|')[1]}</span>
                      )}
                    </span>
                  )}
                  {column.format === 'payment' && row[column.field] && (
                    <span className={`payment ${getPayment(row[column.field])}`}>
                      <FontAwesomeIcon icon={faCcVisa as any} />
                    </span>
                  )}
                  {column.format === 'status' && (
                    <span className={`mo-status ${getStatus(row[column.field])}`}>
                      {row[column.field]}
                    </span>
                  )}
                  {column.format === 'segment' && (
                    <span
                      className="segment cur-poi text-underline text-contrast"
                      onClick={() => props.onDoubleClick && props.onDoubleClick(rowIndex)}
                      style={{ backgroundColor: row.segmentColor, padding: '1px 10px' }}
                    >
                      <span>{row[column.field]}</span>
                    </span>
                  )}
                  {column.format === 'link' && (
                    <span
                      className="link"
                      onClick={() => props.onDoubleClick && props.onDoubleClick(rowIndex)}
                    >
                      {row[column.field]}
                    </span>
                  )}
                  {column.format === 'checkbox' && row[column.field] && (
                    <div className="flex-center">
                      <Checkbox checked={row[column.field]} disabled={true} />
                    </div>
                  )}
                  {column.format === 'html' && column.helpField && (
                    <div dangerouslySetInnerHTML={{ __html: row[column.helpField] }} />
                  )}
                  {column.format === 'jsx' && column.helpField && (
                    <div>{row[column.helpField]}</div>
                  )}
                  {column.format === 'show_more' && (
                    <div
                      className={
                        'mo-table-show-more' +
                        (showMore.includes(rowIndex + column.field) ? ' active' : '')
                      }
                    >
                      {showMore.includes(rowIndex + column.field) ? (
                        <div>
                          <div
                            className="cur-poi"
                            onClick={() =>
                              setShowMore(showMore.filter((it) => it !== rowIndex + column.field))
                            }
                          >
                            <FontAwesomeIcon icon={faMinus} className="mr-5" />
                            {t('show_less')}
                          </div>
                          <div dangerouslySetInnerHTML={{ __html: row[column.field] }} />
                        </div>
                      ) : (
                        <div
                          className="cur-poi"
                          onClick={() => setShowMore([...showMore, rowIndex + column.field])}
                        >
                          <FontAwesomeIcon icon={faPlus} className="mr-5" />
                          {t('show_more')}
                        </div>
                      )}
                    </div>
                  )}

                  {!column.format && <span>{row[column.field]}</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default withErrorBoundary(Table, 'Table');
